import React, { useCallback, useEffect, useRef, useState } from 'react';

// BL-177 Step 2c (A5): ONE save confirmation for the whole app.
//
// A module-level event bus, so a save anywhere can call saveToast.saved() / saveToast.failed(message)
// with no provider in the tree; <SaveToastHost /> is mounted once near the app root and shows it.
// useSaveToast() is the hook form of the same two functions (the identity never changes).
//
// Colours are fixed (not theme tokens) so the contrast holds in both skins:
//   success  #052E16 on #4ADE80  ≈ 9.6:1      error  #FFFFFF on #B91C1C  ≈ 6.5:1
export const SAVED_TOAST_MS = 2000;
export const FAILED_TOAST_MS = 6000;
export const SAVE_TOAST_COLORS = {
    saved: { background: '#4ADE80', color: '#052E16', border: '#16A34A' },
    failed: { background: '#B91C1C', color: '#FFFFFF', border: '#7F1D1D' },
};

const listeners = new Set();
let nextId = 1;

function emit(toast) {
    if (listeners.size === 0) {
        // No host mounted (a test, or a page outside the app shell): never lose a failure silently.
        if (toast.kind === 'failed') console.error(`Save failed: ${toast.message}`);
        return;
    }
    listeners.forEach((listener) => listener({ ...toast, id: nextId++ }));
}

export const saveToast = {
    saved() { emit({ kind: 'saved', message: 'Saved ✓' }); },
    failed(message) { emit({ kind: 'failed', message: message || 'That could not be saved. Please try again.' }); },
};

export function useSaveToast() {
    return saveToast;
}

// BL-177 Step 2f: the toast sits CENTERED AT THE TOP of the page, just below the top bar (the page's first <header>), on every screen.
// A page with no top bar (sign-in) gets it near the top edge. Measured when the toast appears, so it follows the real bar height.
export const TOAST_GAP_PX = 8;
export const TOAST_FALLBACK_TOP_PX = 16;
export function toastTop(doc = typeof document !== 'undefined' ? document : null) {
    try {
        const bar = doc?.querySelector('header');
        const bottom = bar ? Math.round(bar.getBoundingClientRect().bottom) : 0;
        return bottom > 0 && bottom < 200 ? bottom + TOAST_GAP_PX : TOAST_FALLBACK_TOP_PX;
    } catch {
        return TOAST_FALLBACK_TOP_PX;
    }
}

export function SaveToastHost() {
    const [toast, setToast] = useState(null);
    const timer = useRef(null);
    const dismiss = useCallback(() => {
        clearTimeout(timer.current);
        setToast(null);
    }, []);

    useEffect(() => {
        const listener = (next) => {
            clearTimeout(timer.current);
            setToast({ ...next, top: toastTop() });
            timer.current = setTimeout(() => setToast(null), next.kind === 'failed' ? FAILED_TOAST_MS : SAVED_TOAST_MS);
        };
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
            clearTimeout(timer.current);
        };
    }, []);

    if (!toast) return null;
    const colors = SAVE_TOAST_COLORS[toast.kind];
    const failed = toast.kind === 'failed';
    return (
        <div className="fixed inset-x-0 z-[1000] flex justify-center px-4 pointer-events-none" style={{ top: toast.top }} data-save-toast-host="top-center">
            <div
                key={toast.id}
                role={failed ? 'alert' : 'status'}
                data-save-toast={toast.kind}
                style={{ background: colors.background, color: colors.color, border: `1px solid ${colors.border}` }}
                className="pointer-events-auto flex max-w-md items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold shadow-lg"
            >
                <span>{toast.message}</span>
                {failed && (
                    <button
                        type="button"
                        onClick={dismiss}
                        aria-label="Dismiss error"
                        style={{ color: colors.color }}
                        className="ml-1 rounded px-2 py-0.5 underline focus:outline-none focus:ring-2 focus:ring-white"
                    >
                        Dismiss
                    </button>
                )}
            </div>
        </div>
    );
}

export default SaveToastHost;
